import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Plus, Edit, Trash2, ChevronLeft, ChevronRight, FileDown, Copy, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { leaveRequestService } from '@/services/leaveRequestService'
import { employeesService } from '@/services/employeesService'
import { inboxTasksService } from '@/services/inboxTasksService'
import type {
  ApprovalStatusItem,
  Employee,
  LeaveRequestListItem,
  LeaveRequestListResponse,
} from '@/api/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

function formatEmployeeName(e: Employee, employeeFallback: (id: number) => string): string {
  const name = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
  return name || e.empCode?.trim() || (e.id != null ? employeeFallback(e.id) : '—')
}

async function buildEmployeeNameMap(
  branchId: number,
  empIds: number[],
  formatEmployeeNameFn: (e: Employee) => string
): Promise<Record<number, string>> {
  const map: Record<number, string> = {}
  const unique = [...new Set(empIds.filter((id) => id > 0))]
  if (!unique.length) return map

  try {
    const empRes = await employeesService.getAllEmployees(branchId, 1, 10000)
    for (const e of empRes.data || []) {
      const id = e.id ?? 0
      if (!id) continue
      map[id] = formatEmployeeNameFn(e)
    }
  } catch {
    /* ignore */
  }

  const missing = unique.filter((id) => !map[id])
  await Promise.all(
    missing.map(async (id) => {
      try {
        const e = await employeesService.getEmployeeById(branchId, id)
        map[e.id ?? id] = formatEmployeeNameFn(e)
      } catch {
        /* ignore */
      }
    })
  )

  return map
}

export const LeaveRequestList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const employeeFallback = useCallback((id: number) => t('leaveRequestPage.employeeFallback', { id }), [t])
  const formatEmployeeNameFn = useCallback(
    (e: Employee) => formatEmployeeName(e, employeeFallback),
    [employeeFallback]
  )

  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [rows, setRows] = useState<LeaveRequestListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [employeeNameById, setEmployeeNameById] = useState<Record<number, string>>({})
  const [leaveTypeNameById, setLeaveTypeNameById] = useState<Record<number, string>>({})
  const columnVisibility: Record<string, boolean> = {
    employee: true,
    leaveType: true,
    status: true,
    from: true,
    to: true,
    substitute: true,
    approved: true,
    action: true,
  }
  const visibleColumnCount = Object.values(columnVisibility).filter(Boolean).length || 1

  useEffect(() => {
    const load = async () => {
      try {
        const list = await inboxTasksService.getApprovalStatuses()
        setStatuses(list || [])
      } catch {
        setStatuses([])
      }
    }
    load()
  }, [])

  useEffect(() => {
    if (!branchId) return
    let cancelled = false
    const loadTypes = async () => {
      try {
        const types = await leaveRequestService.getLeaveTypes()
        if (cancelled) return
        const m: Record<number, string> = {}
        for (const t of types) {
          const name = (t.name ?? t.nameEn ?? t.lookupNameEn ?? t.lookupNameAr ?? '').trim()
          if (!name) continue
          if (t.id) m[t.id] = name
          if (t.lookupCodeId != null && t.lookupCodeId > 0) m[t.lookupCodeId] = name
        }
        setLeaveTypeNameById(m)
      } catch {
        if (!cancelled) setLeaveTypeNameById({})
      }
    }
    loadTypes()
    return () => {
      cancelled = true
    }
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const codeType = statusFilter === 'all' ? undefined : statusFilter
        const res: LeaveRequestListResponse = await leaveRequestService.getAll(
          branchId,
          pageNumber,
          pageSize,
          codeType
        )
        const list = res.data || []
        setRows(list)
        setTotalRecords(res.totalRecords || 0)

        const empIds: number[] = []
        for (const r of list) {
          if (r.empId && r.empId > 0) empIds.push(r.empId)
          if (r.substituteEmpId && r.substituteEmpId > 0) empIds.push(r.substituteEmpId)
        }
        try {
          const names = await buildEmployeeNameMap(branchId, empIds, formatEmployeeNameFn)
          setEmployeeNameById(names)
        } catch {
          setEmployeeNameById({})
        }
      } catch (e: any) {
        const msg = (e?.response?.data?.message || e?.message || t('leaveRequestPage.fetchFailed')).toString()
        const isNoData = /not found|no data|no records/i.test(msg)
        setRows([])
        setTotalRecords(0)
        setEmployeeNameById({})
        if (!isNoData) toast.error(msg)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize, statusFilter, formatEmployeeNameFn])

  type RowDisplay = LeaveRequestListItem & {
    employeeName: string
    leaveTypeDisplay: string
    substituteDisplay: string
  }

  const rowsDisplay = useMemo((): RowDisplay[] => {
    return rows.map((r) => {
      const ltid = r.leaveTypeId ?? 0
      const leaveTypeDisplay =
        (ltid && leaveTypeNameById[ltid]) || r.leaveTypeName?.trim() || '—'
      const eid = r.empId ?? 0
      const employeeName = eid && employeeNameById[eid] ? employeeNameById[eid] : '—'
      const sid = r.substituteEmpId ?? 0
      const substituteDisplay =
        (sid && employeeNameById[sid]) || r.substituteName?.trim() || '—'
      return {
        ...r,
        employeeName,
        leaveTypeDisplay,
        substituteDisplay,
      }
    })
  }, [rows, employeeNameById, leaveTypeNameById])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rowsDisplay
    const q = searchQuery.toLowerCase()
    return rowsDisplay.filter(
      (r) =>
        String(r.id).toLowerCase().includes(q) ||
        (r.employeeName ?? '').toLowerCase().includes(q) ||
        (r.leaveTypeDisplay ?? '').toLowerCase().includes(q) ||
        (r.statusName ?? '').toLowerCase().includes(q) ||
        (r.fromDate ?? '').toLowerCase().includes(q) ||
        (r.toDate ?? '').toLowerCase().includes(q) ||
        (r.substituteDisplay ?? '').toLowerCase().includes(q) ||
        (r.approvedDate ?? '').toLowerCase().includes(q)
    )
  }, [rowsDisplay, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return String(dateStr).slice(0, 10)
    }
  }

  const statusBadge = (row: LeaveRequestListItem) => {
    const statusCode = Number(row.status ?? 0)
    let text: string
    if (statusCode === 434) text = t('leaveRequestPage.statusPending')
    else if (statusCode === 435) text = t('leaveRequestPage.statusApproved')
    else if (statusCode === 436) text = t('leaveRequestPage.statusRejected')
    else text = row.statusName ?? (row.status != null ? String(row.status) : '—')

    const isApproved = statusCode === 435
    const isPending = statusCode === 434
    return (
      <Badge variant={isApproved ? 'default' : isPending ? 'secondary' : 'outline'} className="text-xs">
        {text}
      </Badge>
    )
  }

  const csvEscape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`

  const handleCopy = async () => {
    if (filteredRows.length === 0) {
      toast.error(t('leaveRequestPage.nothingToCopy', { defaultValue: 'Nothing to copy.' }))
      return
    }
    const header = [
      t('leaveRequestPage.colEmployee'),
      t('leaveRequestPage.colLeaveType'),
      t('leaveRequestPage.colFrom'),
      t('leaveRequestPage.colTo'),
      t('leaveRequestPage.colSubstitute'),
      t('leaveRequestPage.colStatus'),
      t('leaveRequestPage.colApproved'),
    ].join('\t')
    const lines = filteredRows.map((r) =>
      [
        r.employeeName ?? '',
        r.leaveTypeDisplay ?? '',
        String(r.fromDate ?? '').slice(0, 10),
        String(r.toDate ?? '').slice(0, 10),
        r.substituteDisplay ?? '',
        String(r.statusName ?? r.status ?? ''),
        String(r.approvedDate ?? '').slice(0, 10),
      ].join('\t')
    )
    await navigator.clipboard.writeText([header, ...lines].join('\n'))
    toast.success(t('leaveRequestPage.copiedToClipboard', { defaultValue: 'Copied to clipboard.' }))
  }

  const handleExportCSV = () => {
    if (filteredRows.length === 0) {
      toast.error(t('leaveRequestPage.nothingToExport', { defaultValue: 'Nothing to export.' }))
      return
    }
    const header = [
      t('leaveRequestPage.colEmployee'),
      t('leaveRequestPage.colLeaveType'),
      t('leaveRequestPage.colFrom'),
      t('leaveRequestPage.colTo'),
      t('leaveRequestPage.colSubstitute'),
      t('leaveRequestPage.colStatus'),
      t('leaveRequestPage.colApproved'),
    ]
      .map(csvEscape)
      .join(',')
    const body = filteredRows
      .map((r) =>
        [
          r.employeeName ?? '',
          r.leaveTypeDisplay ?? '',
          String(r.fromDate ?? '').slice(0, 10),
          String(r.toDate ?? '').slice(0, 10),
          r.substituteDisplay ?? '',
          String(r.statusName ?? r.status ?? ''),
          String(r.approvedDate ?? '').slice(0, 10),
        ]
          .map(csvEscape)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `leave_requests_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('leaveRequestPage.csvDownloaded', { defaultValue: 'CSV downloaded.' }))
  }

  const handlePrint = () => window.print()

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(undefined, t('leaveRequestPage.deleteConfirm'))
    if (!confirmed) return
    try {
      await leaveRequestService.delete(id)
      await showSuccess(t('leaveRequestPage.deletedTitle'), t('leaveRequestPage.deletedMessage'))
      if (branchId) {
        const res = await leaveRequestService.getAll(branchId, pageNumber, pageSize, statusFilter === 'all' ? undefined : statusFilter)
        const list = res.data || []
        setRows(list)
        setTotalRecords(res.totalRecords || 0)
        const empIds: number[] = []
        for (const r of list) {
          if (r.empId && r.empId > 0) empIds.push(r.empId)
          if (r.substituteEmpId && r.substituteEmpId > 0) empIds.push(r.substituteEmpId)
        }
        try {
          setEmployeeNameById(await buildEmployeeNameMap(branchId, empIds, formatEmployeeNameFn))
        } catch {
          setEmployeeNameById({})
        }
      }
    } catch (e: any) {
      await showError(t('leaveRequestPage.deleteFailedTitle'), e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.deleteFailedMessage'))
    }
  }

  const req = t('leaveRequestPage.requiredMark')

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('leaveRequestPage.pageTitle')} description={t('leaveRequestPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">{t('leaveRequestPage.selectBranchFirst')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('leaveRequestPage.pageTitle')}
        description={t('leaveRequestPage.pageDescription')}
        action={{
          label: t('leaveRequestPage.addRequest'),
          onClick: () => navigate('/self-services/leave-requests/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  {req} {t('leaveRequestPage.labelApprovalStatus')}
                </label>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPageNumber(1) }}>
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder={t('leaveRequestPage.placeholderAll')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('leaveRequestPage.placeholderAll')}</SelectItem>
                    {statuses.filter((s) => s.id).map((s) => {
                      const value = String(s.lookupCodeId ?? s.id ?? 0)
                      const label = s.lookupNameEn || s.lookupNameAr || value
                      return (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('leaveRequestPage.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={handleExportCSV}>
                  <FileDown className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                  <Copy className="mr-2 h-4 w-4" />
                  {t('leaveRequestPage.copy', { defaultValue: 'Copy' })}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  {t('leaveRequestPage.print', { defaultValue: 'Print' })}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {columnVisibility.employee && (
                      <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('leaveRequestPage.colEmployee')}</TableHead>
                    )}
                    {columnVisibility.leaveType && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('leaveRequestPage.colLeaveType')}</TableHead>}
                    {columnVisibility.from && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('leaveRequestPage.colFrom')}</TableHead>}
                    {columnVisibility.to && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('leaveRequestPage.colTo')}</TableHead>}
                    {columnVisibility.substitute && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('leaveRequestPage.colSubstitute')}</TableHead>}
                    {columnVisibility.status && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">{t('leaveRequestPage.colStatus')}</TableHead>}
                    {columnVisibility.approved && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('leaveRequestPage.colApproved')}</TableHead>}
                    {columnVisibility.action && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-28">{t('leaveRequestPage.colAction')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnCount} className="text-center py-12 text-muted-foreground">
                        {searchQuery.trim() ? t('leaveRequestPage.noMatchSearch') : t('leaveRequestPage.noData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id} className="border-b hover:bg-primary/5">
                        {columnVisibility.employee && (
                          <TableCell className="px-4 py-2">{row.employeeName}</TableCell>
                        )}
                        {columnVisibility.leaveType && (
                          <TableCell className="px-4 py-2">{row.leaveTypeDisplay}</TableCell>
                        )}
                        {columnVisibility.from && <TableCell className="px-4 py-2">{formatDate(row.fromDate)}</TableCell>}
                        {columnVisibility.to && <TableCell className="px-4 py-2">{formatDate(row.toDate)}</TableCell>}
                        {columnVisibility.substitute && (
                          <TableCell className="px-4 py-2">{row.substituteDisplay}</TableCell>
                        )}
                        {columnVisibility.status && <TableCell className="px-4 py-2 text-center">{statusBadge(row)}</TableCell>}
                        {columnVisibility.approved && <TableCell className="px-4 py-2">{formatDate(row.approvedDate)}</TableCell>}
                        {columnVisibility.action && (
                          <TableCell className="px-4 py-2 text-center">
                            <div className="inline-flex gap-1">
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-primary hover:bg-primary/10" onClick={() => navigate(`/self-services/leave-requests/${row.id}`)} title={t('leaveRequestPage.editTitle')}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => handleDelete(row.id)} title={t('leaveRequestPage.deleteTitle')}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  {t('leaveRequestPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber <= 1}>
                    <ChevronLeft className="h-4 w-4" /> {t('leaveRequestPage.previous')}
                  </Button>
                  <span className="text-sm">{t('leaveRequestPage.pageOf', { page: pageNumber, totalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))} disabled={pageNumber >= totalPages}>
                    {t('leaveRequestPage.next')} <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
